/**
 * Value formatters used by SmartCell renderers.
 *
 * Single source of truth — every cell type goes through one of these so
 * that a downloaded CSV preview, a tooltip and the cell itself agree.
 */

import type { ColumnMeta } from './types';

// ─────────────────────────────────────────────────────────────────────────────
// Numbers
// ─────────────────────────────────────────────────────────────────────────────

const COMPACT_THRESHOLDS: Array<{ v: number; suffix: string }> = [
  { v: 1_000_000_000, suffix: 'B' },
  { v: 1_000_000, suffix: 'M' },
  { v: 1_000, suffix: 'K' },
];

/** Plain number formatting — preserves integers, max 4 fractional digits. */
export function formatNumber(value: number): string {
  if (!Number.isFinite(value)) return '';
  if (Number.isInteger(value)) return value.toLocaleString();
  // At most two decimals, everywhere a result is shown (Anuj, 29 Oct 2026).
  return value.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

/**
 * Compact number — `1.2K`, `3.4M`, `1.05B`. Used by Currency cells when
 * the magnitude is large so a column doesn't need to be 200px wide.
 */
export function formatCompact(value: number, fractionDigits: number = 2): string {
  if (!Number.isFinite(value)) return '';
  const abs = Math.abs(value);
  for (const { v, suffix } of COMPACT_THRESHOLDS) {
    if (abs >= v) {
      const scaled = value / v;
      // Drop trailing zeros for a cleaner look.
      const fixed = scaled.toFixed(fractionDigits);
      return parseFloat(fixed) + suffix;
    }
  }
  return Math.abs(value) >= 100
    ? Math.round(value).toLocaleString()
    : value.toLocaleString(undefined, { maximumFractionDigits: fractionDigits });
}

// ─────────────────────────────────────────────────────────────────────────────
// Currency
// ─────────────────────────────────────────────────────────────────────────────

const CURRENCY_SYMBOLS: Record<string, string> = {
  USD: '$',
  EUR: '€',
  GBP: '£',
  INR: '₹',
  JPY: '¥',
  CNY: '¥',
  AUD: 'A$',
  CAD: 'C$',
  CHF: 'CHF ',
};

export function currencySymbol(code?: string): string {
  if (!code) return '$';
  return CURRENCY_SYMBOLS[code.toUpperCase()] || code.toUpperCase() + ' ';
}

export function formatCurrency(value: number, code?: string, compact?: boolean): string {
  if (!Number.isFinite(value)) return '';
  const sym = currencySymbol(code);
  const abs = Math.abs(value);
  if (compact && abs >= 1000) {
    return (value < 0 ? '-' : '') + sym + formatCompact(abs);
  }
  if (Number.isInteger(value)) {
    return (value < 0 ? '-' : '') + sym + abs.toLocaleString();
  }
  return (
    (value < 0 ? '-' : '') +
    sym +
    abs.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Percent
// ─────────────────────────────────────────────────────────────────────────────

export function formatPercent(value: number, scale: 'fraction' | 'whole' = 'whole'): string {
  if (!Number.isFinite(value)) return '';
  const v = scale === 'fraction' ? value * 100 : value;
  if (Math.abs(v) >= 100 && Number.isInteger(v)) return v.toFixed(0) + '%';
  return v.toFixed(2).replace(/\.?0+$/, '') + '%';
}

/**
 * A single figure on a metric tile, formatted as its name suggests: a rate,
 * share or percent as a percentage (0.4554 → 45.54%, 9.55 → 9.55%), a large
 * number shortened (1.2M), anything else with at most two decimals.
 */
export function formatMetricFigure(value: unknown, nameHint: string = ''): string {
  if (value === null || value === undefined || value === '') return '—';
  const v = typeof value === 'number' ? value : Number(value);
  if (!Number.isFinite(v)) return String(value);
  if (/(pct|percent|rate|ratio|share)/i.test(nameHint)) {
    return formatPercent(v, Math.abs(v) <= 1 ? 'fraction' : 'whole');
  }
  if (Math.abs(v) >= 1_000_000) return formatCompact(v);
  return formatNumber(v);
}

// ─────────────────────────────────────────────────────────────────────────────
// Dates
// ─────────────────────────────────────────────────────────────────────────────

function toDate(value: any): Date | null {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  if (typeof value === 'number') {
    // Heuristic: epoch seconds vs ms. Anything < 1e12 ⇒ seconds.
    const ms = value < 1e12 ? value * 1000 : value;
    const d = new Date(ms);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'string') {
    // A date-only string (`YYYY-MM-DD`) is parsed by `new Date()` as UTC
    // midnight, which `toLocaleDateString` then shifts into the viewer's
    // local zone — dropping a calendar day for any negative UTC offset
    // (e.g. the Americas). Pin those to local midnight so the rendered day
    // matches the value the database actually returned.
    const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (dateOnly) {
      const [, y, m, day] = dateOnly;
      const d = new Date(Number(y), Number(m) - 1, Number(day));
      return Number.isNaN(d.getTime()) ? null : d;
    }
    const d = new Date(value);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  return null;
}

export function formatDate(value: any): string {
  const d = toDate(value);
  if (!d) return String(value ?? '');
  return d.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function formatDateTime(value: any): string {
  const d = toDate(value);
  if (!d) return String(value ?? '');
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function relativeDate(value: any): string {
  const d = toDate(value);
  if (!d) return '';
  const diffMs = Date.now() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  const abs = Math.abs(sec);
  const future = sec < 0;
  if (abs < 60) return future ? 'in seconds' : 'just now';
  const mins = Math.round(abs / 60);
  if (mins < 60) return future ? `in ${mins}m` : `${mins}m ago`;
  const hrs = Math.round(mins / 60);
  if (hrs < 24) return future ? `in ${hrs}h` : `${hrs}h ago`;
  const days = Math.round(hrs / 24);
  if (days < 30) return future ? `in ${days}d` : `${days}d ago`;
  const months = Math.round(days / 30);
  if (months < 12) return future ? `in ${months}mo` : `${months}mo ago`;
  const years = Math.round(months / 12);
  return future ? `in ${years}y` : `${years}y ago`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Generic / sort-friendly value
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Best-effort string for sorting / search. Works for every field type.
 * Always returns a string so DataGrid's default comparator behaves.
 */
export function formatForSort(value: any, meta: ColumnMeta): string | number {
  if (value === null || value === undefined) return '';
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (meta.type === 'date' || meta.type === 'datetime') {
    const d = toDate(value);
    return d ? d.getTime() : 0;
  }
  if (Array.isArray(value)) return value.join(', ').toLowerCase();
  return String(value).toLowerCase();
}

/** Plain-text fallback. Used for tooltips and CSV. */
export function formatPlainText(value: any, meta: ColumnMeta): string {
  if (value === null || value === undefined) return '';
  switch (meta.type) {
    case 'number': return typeof value === 'number' ? formatNumber(value) : String(value);
    case 'currency': return typeof value === 'number'
      ? formatCurrency(value, meta.currency)
      : String(value);
    case 'percent': return typeof value === 'number'
      ? formatPercent(value, meta.percentScale)
      : String(value);
    case 'date': return formatDate(value);
    case 'datetime': return formatDateTime(value);
    case 'boolean': return value ? 'Yes' : 'No';
    case 'json': {
      try {
        return typeof value === 'string' ? value : JSON.stringify(value);
      } catch {
        return String(value);
      }
    }
    default: {
      if (Array.isArray(value)) return value.map(String).join(', ');
      return String(value);
    }
  }
}
